import React from 'react';

import { getTextField, ValidationError } from '@lender/utils';
import { IconButton } from '@mui/material';
import { CalculatorIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import { compose } from 'redux';

import { COMMON_DATE_FORMAT, TODAY_IN_DAYS } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import {
  LiabilitiesType,
  LienPriorityType,
  LoanType,
  MAX_REMAINING_MONTHS_NUMBER,
  PaymentExclusionReasonType,
  SourceOfFundsType,
  TypeOfCredit,
} from 'constants/loanApplication';
import {
  liabilitiesTypesOptions,
  liabilityExpenseTypeOptions,
  liabilityLoanTypeOptions,
  paymentExclusionReasonOptions,
  sourceOfFundsOptions,
  typeOfCreditOptions,
} from 'constants/options';
import { getConditionalArray } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { FinancialInfoTestIds } from '../../../../FinancialInfo.testIds';
import { ownersFieldCustomStyles } from '../../Content.styles';
import { checkboxStyles } from '../SectionContent.styles';
import { getPlaceholder } from '../SectionContent.utils';

const FHA_CASE_NUMBER_MIN_LENGTH = 10;
const BASE_YEAR = 1900;

export const getConfigFields = ({ recordId, ownersOptions, isNewRecord }) => ({
  owners: {
    fieldId: 'owners',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].owners`,
    inputProps: {
      placeholder: getPlaceholder(isNewRecord, 'Choose Borrower'),
      disabled: false,
      required: true,
      multiple: true,
      variant: 'outlined',
      isOptionEqualToValue: (option, value) => isEqual(option, value),
      options: ownersOptions,
      'data-test-value': null,
      columns: 4,
      containerCustomStyles: ownersFieldCustomStyles,
      isOneRow: true,
    },
  },
  liabilityType: {
    fieldId: 'type',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].type`,
    inputProps: {
      label: 'Liability Type',
      placeholder: getPlaceholder(isNewRecord, 'Liability Type'),
      disabled: false,
      required: true,
      variant: 'outlined',
      options: liabilitiesTypesOptions,
      'data-test-value': liabilitiesTypesOptions[0].value,
    },
  },
  creditorName: {
    fieldId: 'companyName',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].companyName`,
    inputProps: {
      label: 'Creditor Name',
      placeholder: getPlaceholder(isNewRecord, 'Creditor Name'),
      required: true,
      variant: 'outlined',
      type: FieldInputType.TEXT,
      'data-test-value': 'creditor-name',
      'data-test-edited-value': 'creditor-name-edited',
    },
  },
  liabilityExpenseType: {
    fieldId: 'typeOfCredit',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].liabilityExpenseType`,
    inputProps: {
      options: liabilityExpenseTypeOptions,
      label: 'Type of credit',
      placeholder: getPlaceholder(isNewRecord, 'Type of credit'),
      required: true,
      variant: 'outlined',
    },
  },
  accountType: {
    fieldId: 'typeOfCredit',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].accountType`,
    inputProps: {
      options: typeOfCreditOptions,
      label: 'Type of credit',
      placeholder: getPlaceholder(isNewRecord, 'Type of credit'),
      required: true,
      variant: 'outlined',
    },
  },
  loanType: {
    fieldId: 'loanType',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].loanType`,
    inputProps: {
      options: liabilityLoanTypeOptions,
      label: 'Type of Loan',
      placeholder: getPlaceholder(isNewRecord, 'Type of Loan'),
      required: false,
      variant: 'outlined',
    },
  },
  existingCaseNumber: {
    fieldId: 'existingCaseNumber',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].existingCaseNumber`,
    inputProps: {
      autoComplete: 'off',
      placeholder: getPlaceholder(isNewRecord, 'Existing Case #'),
      label: 'Existing Case #',
      disabled: false,
      required: true,
      customSchema: () =>
        getTextField({ isRequired: true }).min(
          FHA_CASE_NUMBER_MIN_LENGTH,
          'Field value should be a valid ten-digit Case Number',
        ),
      variant: 'outlined',
      type: FieldInputType.FHA_CASE_NUMBER,
      dataTestId: FinancialInfoTestIds.LIABILITIES_FORM_FHA_CASE_NUMBER_INPUT,
      'data-test-value': '1234567890',
      'data-test-formatted-value': '123-4567890',
    },
  },
  existingLienUpfrontMip: {
    fieldId: 'existingLienUpfrontMip',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].existingLienUpfrontMip`,
    inputProps: {
      placeholder: getPlaceholder(isNewRecord, 'Existing Lien Upfront MIP'),
      label: 'Existing Lien Upfront MIP',
      disabled: false,
      required: true,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
      dataTestId: FinancialInfoTestIds.LIABILITIES_FORM_FHA_LIEN_UPFRONT_MIP_INPUT,
    },
  },
  existingLienInterestAmount: {
    fieldId: 'existingLienInterestAmount',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].existingLienInterestAmount`,
    inputProps: {
      placeholder: getPlaceholder(isNewRecord, 'Existing Lien Interest Amount'),
      label: 'Existing Lien Interest Amount',
      disabled: false,
      required: true,
      variant: 'outlined',
      type: FieldInputType.PERCENTAGE_RESTYLED,
      decimalScale: 3,
      decimalSeparator: '.',
      fixedDecimalScale: true,
      isNumericString: true,
      dataTestId: FinancialInfoTestIds.LIABILITIES_FORM_LIEN_INTEREST_AMOUNT_INPUT,
    },
  },
  hudAuthorizationCode: {
    fieldId: 'hudAuthorizationCode',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].hudAuthorizationCode`,
    inputProps: {
      autoComplete: 'off',
      placeholder: getPlaceholder(isNewRecord, 'HUD Authorization Code'),
      label: 'HUD Authorization Code',
      disabled: false,
      required: true,
      variant: 'outlined',
      type: FieldInputType.TEXT_SPECIFIC_PATTERN,
      validationMessage: ValidationError.SpecialSymbolsNotAllowed,
      patternRegExp: /^[a-zA-Z0-9]*$/,
      maxLength: 100,
      dataTestId: FinancialInfoTestIds.LIABILITIES_FORM_HUDAUTHORIZATIONCODE_INPUT,
    },
  },
  accountNumber: {
    fieldId: 'accountNumber',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].accountNumber`,
    inputProps: {
      label: 'Account Number',
      placeholder: getPlaceholder(isNewRecord, 'Account Number'),
      required: true,
      type: FieldInputType.TEXT_EXCEPT_SPECIAL_SYMBOLS,
      variant: 'outlined',
    },
  },
  sourceOfFunds: {
    fieldId: 'sourceOfFunds',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].sourceOfFunds`,
    inputProps: {
      options: sourceOfFundsOptions,
      label: 'Source Of Funds',
      placeholder: 'Select One',
      required: false,
      variant: 'outlined',
    },
  },
  otherFundDescription: {
    fieldId: 'otherFundDescription',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].otherFundDescription`,
    inputProps: {
      label: 'Description',
      placeholder: getPlaceholder(isNewRecord, 'Description'),
      required: false,
      type: FieldInputType.TEXT,
      variant: 'outlined',
    },
  },
  monthsDeferred: {
    fieldId: 'monthsDeferred',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].monthsDeferred`,
    inputProps: {
      label: 'Months Deferred',
      placeholder: getPlaceholder(isNewRecord, 'Months Deferred'),
      required: false,
      variant: 'outlined',
      minValue: 0,
      type: FieldInputType.INT,
    },
  },
  lienPriorityType: {
    fieldId: 'lienPriorityType',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].lienPriorityType`,
    inputProps: {
      options: [],
      label: 'Lien Priority',
      placeholder: 'Select One',
      required: false,
      variant: 'outlined',
    },
  },
  unpaidBalance: {
    fieldId: 'unpaidBalance',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].unpaidBalance`,
    inputProps: {
      label: 'Unpaid Balance',
      placeholder: getPlaceholder(isNewRecord, '0.00'),
      disabled: false,
      required: true,
      variant: 'outlined',
      minValue: 0,
      type: FieldInputType.CURRENCY,
      'data-test-value': '$999',
    },
  },
  monthlyPayment: {
    fieldId: 'monthlyPayment',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].monthlyPayment`,
    inputProps: {
      label: 'Monthly Payment',
      placeholder: getPlaceholder(isNewRecord, '0.00'),
      disabled: false,
      required: true,
      variant: 'outlined',
      minValue: 0,
      type: FieldInputType.CURRENCY,
      'data-test-value': '$999',
    },
  },
  highLimit: {
    fieldId: 'highLimit',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].highLimit`,
    inputProps: {
      label: 'High Limit',
      placeholder: getPlaceholder(isNewRecord, '1.00'),
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
      'data-test-value': '$999',
    },
  },
  remainingMonths: {
    fieldId: 'monthsRemaining',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].monthsRemaining`,
    inputProps: {
      label: 'Remaining Months',
      placeholder: getPlaceholder(isNewRecord, 'Remaining Months'),
      required: false,
      variant: 'outlined',
      minValue: 0,
      maxValue: MAX_REMAINING_MONTHS_NUMBER,
      type: FieldInputType.INT,
    },
  },
  propertyAddress: {
    fieldId: 'property',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].property`,
    inputProps: {
      label: 'Property Address',
      placeholder: getPlaceholder(isNewRecord, 'Property Address'),
      disabled: false,
      required: false,
      variant: 'outlined',
      options: [],
      'data-test-value': null,
    },
  },
  dateOpened: {
    fieldId: 'dateOpened',
    type: FieldType.DATE,
    nameInModel: `[${recordId}].dateOpened`,
    inputProps: {
      label: 'Open Date',
      disabled: false,
      required: false,
      variant: 'outlined',
      format: COMMON_DATE_FORMAT,
      maxDate: dayjs(TODAY_IN_DAYS).add(-1, 'day'),
      type: FieldInputType.DATE,
      'data-test-value': dayjs().set('year', BASE_YEAR),
    },
  },
  isSubordinate: {
    fieldId: 'isSubordinate',
    type: FieldType.CHECKBOX,
    nameInModel: `[${recordId}].isSubordinate`,
    inputProps: {
      customStyles: checkboxStyles,
      iconProps: {
        height: '20',
        width: '20',
      },
      label: 'Subordinate',
      'data-testid': FinancialInfoTestIds.LIABILITIES_FORM_IS_SUBORDINATE_CHECKBOX,
      'data-test-value': false,
    },
  },
  paidOff: {
    fieldId: 'toBePaidOffAtOrBeforeClosing',
    type: FieldType.CHECKBOX,
    nameInModel: `[${recordId}].toBePaidOffAtOrBeforeClosing`,
    inputProps: {
      customStyles: checkboxStyles,
      iconProps: {
        height: '20',
        width: '20',
      },
      label: 'Pay Off',
      'data-testid': FinancialInfoTestIds.LIABILITIES_FORM_PAY_OFF_CHECKBOX,
      'data-test-value': false,
    },
  },
  qualifiedNonCashOut: {
    fieldId: 'qualifiedNonCashOut',
    type: FieldType.CHECKBOX,
    nameInModel: `[${recordId}].qualifiedNonCashOut`,
    inputProps: {
      customStyles: checkboxStyles,
      iconProps: {
        height: '20',
        width: '20',
      },
      label: 'Non Cash Out',
      'data-testid': FinancialInfoTestIds.LIABILITIES_FORM_NON_CASH_OUT_CHECKBOX,
      'data-test-value': false,
    },
  },
  excludePayment: {
    fieldId: 'excludePayment',
    type: FieldType.CHECKBOX,
    nameInModel: `[${recordId}].excludePayment`,
    inputProps: {
      customStyles: checkboxStyles,
      iconProps: {
        height: '20',
        width: '20',
      },
      label: 'Exclude Payment',
      'data-testid': FinancialInfoTestIds.LIABILITIES_FORM_EXCLUDE_PAYMENT_CHECKBOX,
      'data-test-value': false,
    },
  },
  isAffordableLoan: {
    fieldId: 'isAffordableLoan',
    type: FieldType.CHECKBOX,
    nameInModel: `[${recordId}].isAffordableLoan`,
    inputProps: {
      customStyles: checkboxStyles,
      iconProps: {
        height: '20',
        width: '20',
      },
      label: 'Affordable Loan',
      'data-testid': FinancialInfoTestIds.LIABILITIES_FORM_AFFORDABLE_LOAN_CHECKBOX,
      'data-test-value': false,
    },
  },
  paymentExclusionReason: {
    fieldId: 'paymentExclusionReason',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].paymentExclusionReason`,
    inputProps: {
      options: paymentExclusionReasonOptions,
      label: 'Exclusion Reason',
      placeholder: getPlaceholder(isNewRecord, 'Exclusion Reason'),
      required: true,
      variant: 'outlined',
    },
  },
  paymentExclusionReasonOtherDescription: {
    fieldId: 'paymentExclusionReasonOtherDescription',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].paymentExclusionReasonOtherDescription`,
    inputProps: {
      label: 'Other Reason',
      placeholder: getPlaceholder(isNewRecord, 'Other Reason'),
      required: true,
      type: FieldInputType.TEXT,
      variant: 'outlined',
      maxLength: 150,
    },
  },
  isOriginalLenderRefinance: {
    fieldId: 'isOriginalLenderRefinance',
    type: FieldType.CHECKBOX,
    nameInModel: `[${recordId}].isOriginalLenderRefinance`,
    inputProps: {
      customStyles: checkboxStyles,
      iconProps: {
        height: '20',
        width: '20',
      },
      label: 'Original Lender Refinance',
      'data-testid': FinancialInfoTestIds.LIABILITIES_FORM_ORIGINAL_LENDER_REFINANCE_CHECKBOX,
      'data-test-value': false,
    },
  },
  heloc: {
    fieldId: 'heloc',
    type: FieldType.CHECKBOX,
    nameInModel: `[${recordId}].heloc`,
    inputProps: {
      customStyles: checkboxStyles,
      iconProps: {
        height: '20',
        width: '20',
      },
      label: 'HELOC',
      'data-testid': FinancialInfoTestIds.LIABILITIES_FORM_HELOC_CHECKBOX,
      'data-test-value': false,
    },
  },
  closingCosts: {
    fieldId: 'closingCosts',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].closingCosts`,
    inputProps: {
      label: 'Closing Costs',
      placeholder: getPlaceholder(isNewRecord, '0.00'),
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
    },
  },
  servicingContactName: {
    fieldId: 'servicingContactName',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].servicingContactName`,
    inputProps: {
      label: 'Servicing Contact Name',
      placeholder: getPlaceholder(isNewRecord, 'Servicing Contact Name'),
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEXT,
    },
  },
  servicingContactPhoneNumber: {
    fieldId: 'servicingContactPhoneNumber',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].servicingContactPhoneNumber`,
    inputProps: {
      label: 'Servicing Contact Phone',
      placeholder: getPlaceholder(isNewRecord, 'Servicing Contact Phone'),
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEL,
    },
  },
  originalPropertyValue: {
    fieldId: 'originalPropertyValue',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].originalPropertyValue`,
    inputProps: {
      label: 'Original Property Value',
      placeholder: getPlaceholder(isNewRecord, '0.00'),
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
    },
  },
  loanMipFactor: {
    fieldId: 'loanMipFactor',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].loanMipFactor`,
    inputProps: {
      label: 'Loan MIP Factor',
      placeholder: getPlaceholder(isNewRecord, '0.000'),
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.PERCENTAGE_RESTYLED,
      decimalScale: 3,
      decimalSeparator: '.',
      fixedDecimalScale: true,
      isNumericString: true,
    },
  },
  endorsementDate: {
    fieldId: 'endorsementDate',
    type: FieldType.DATE,
    nameInModel: `[${recordId}].endorsementDate`,
    inputProps: {
      label: 'Endorsement Date',
      disabled: false,
      required: false,
      variant: 'outlined',
      format: COMMON_DATE_FORMAT,
      type: FieldInputType.DATE,
    },
  },
  originalEndorsementDate: {
    fieldId: 'originalEndorsementDate',
    type: FieldType.DATE,
    nameInModel: `[${recordId}].originalEndorsementDate`,
    inputProps: {
      label: 'Original Endorsement Date',
      disabled: false,
      required: false,
      variant: 'outlined',
      format: COMMON_DATE_FORMAT,
      type: FieldInputType.DATE,
    },
  },
  unearnedMipFactor: {
    fieldId: 'unearnedMipFactor',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].unearnedMipFactor`,
    inputProps: {
      label: 'Unearned MIP Factor',
      placeholder: '0.00',
      disabled: true,
      required: false,
      variant: 'outlined',
      type: FieldInputType.PERCENTAGE_RESTYLED,
      decimalScale: 2,
      decimalSeparator: '.',
      fixedDecimalScale: true,
      isNumericString: true,
    },
  },
  unearnedMip: {
    fieldId: 'unearnedMip',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].unearnedMip`,
    inputProps: {
      label: 'Unearned MIP',
      placeholder: '0.00',
      disabled: true,
      required: false,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
    },
  },
  earnedMip: {
    fieldId: 'earnedMip',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].earnedMip`,
    inputProps: {
      label: 'Earned MIP',
      placeholder: '0.00',
      disabled: true,
      required: false,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
    },
  },
});

const accountTypesWithoutRemainingMonths = [
  TypeOfCredit.OPEN_30_DAY,
  TypeOfCredit.COLLECTION,
  TypeOfCredit.OTHER,
];

const accountTypesWithProperty = [TypeOfCredit.LINE_OF_CREDIT, TypeOfCredit.MORTGAGE];

// LEN-205: the lien on a chattel home reports as an installment account. On a chattel loan it links to the subject
// property, with a lien priority, as the home lien a chattel refinance pays off.
const chattelAccountTypesWithProperty = [...accountTypesWithProperty, TypeOfCredit.INSTALLMENT];

const accountTypesWithLoanType = [TypeOfCredit.LINE_OF_CREDIT, TypeOfCredit.MORTGAGE];

const accountTypesWithHightLimit = [
  TypeOfCredit.REVOLVING,
  TypeOfCredit.OPEN_30_DAY,
  TypeOfCredit.COLLECTION,
  TypeOfCredit.OTHER,
];

export const accountTypesWithMortgageDetails = [TypeOfCredit.MORTGAGE, TypeOfCredit.LINE_OF_CREDIT];

export const getAccountTypeFlags = (accountType, { isChattelLoan = false } = {}) => ({
  withLoanType: accountTypesWithLoanType.includes(accountType),
  withRemainingMonths: !accountTypesWithoutRemainingMonths.includes(accountType),
  withProperty: (isChattelLoan
    ? chattelAccountTypesWithProperty
    : accountTypesWithProperty
  ).includes(accountType),
  withHighLimit: accountTypesWithHightLimit.includes(accountType),
  withHELOC: accountType === TypeOfCredit.LINE_OF_CREDIT,
  withMortgageDetails: accountTypesWithMortgageDetails.includes(accountType),
  withChattelHomeLien: isChattelLoan && accountType === TypeOfCredit.INSTALLMENT,
});

export const checkIsFHADetailsVisible = ({ isRefinanceFlow, accountType, loanType }) =>
  isRefinanceFlow && accountType === TypeOfCredit.MORTGAGE && loanType === LoanType.FHA;

export const checkIsSubjectPropertyMortgage = ({ withMortgageDetails, isSubjectProperty }) =>
  withMortgageDetails && isSubjectProperty;

export const getConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    recordId,
    liabilityType,
    accountType,
    sourceOfFunds,
    isHeloc,
    isPaidOff,
    isExcludePayment,
    paymentExclusionReason,
    propertiesOptions,
    lienPriorityTypeOptions,
    lienPriorityType,
    ownersOptions,
    isNewRecord,
    loanType,
    isRefinanceFlow,
    isOwnersClicked,
    isMortgageClicked,
    isSubjectProperty,
    onCalculateMonthlyPayment,
    shouldForcePaidOffAndNonCashOut,
    isChattelLoan = false,
  }) => {
    const liabilitiesConfigFields = getConfigFields({
      recordId,
      ownersOptions,
      isNewRecord,
    });

    const isOtherLiabilityType = liabilityType === LiabilitiesType.OTHER;
    const isLiabilityType = liabilityType === LiabilitiesType.LIABILITY;
    const isOtherSourceOfFunds = sourceOfFunds === SourceOfFundsType.OTHER;
    const isOtherExclusionReason = paymentExclusionReason === PaymentExclusionReasonType.OTHER;

    const {
      withHELOC,
      withRemainingMonths,
      withLoanType,
      withProperty,
      withHighLimit,
      withMortgageDetails,
      withChattelHomeLien,
    } = getAccountTypeFlags(accountType, { isChattelLoan });

    const withFHADetails = checkIsFHADetailsVisible({ isRefinanceFlow, accountType, loanType });
    const withSubjectPropertyMortgageDetails = checkIsSubjectPropertyMortgage({
      withMortgageDetails,
      isSubjectProperty,
    });

    return {
      groups: [
        ...getConditionalArray(isOwnersClicked || isNewRecord, [
          {
            type: FieldType.GROUP,
            fields: [liabilitiesConfigFields.owners],
            key: `${recordId}-owners`,
          },
        ]),
        {
          type: FieldType.GROUP,
          fields: [
            {
              ...liabilitiesConfigFields.liabilityType,
              inputProps: {
                ...liabilitiesConfigFields.liabilityType.inputProps,
                disabled: isMortgageClicked,
              },
            },
          ],
          key: `${recordId}-liabilityType`,
        },
        {
          type: FieldType.GROUP,
          fields: [
            ...getConditionalArray(isLiabilityType, () => [
              liabilitiesConfigFields.creditorName,
              {
                ...liabilitiesConfigFields.accountType,
                inputProps: {
                  ...liabilitiesConfigFields.accountType.inputProps,
                  options: isMortgageClicked
                    ? typeOfCreditOptions.filter(({ value }) =>
                        [TypeOfCredit.MORTGAGE, TypeOfCredit.LINE_OF_CREDIT].includes(value),
                      )
                    : typeOfCreditOptions,
                },
              },
              ...getConditionalArray(withLoanType, [liabilitiesConfigFields.loanType]),
              liabilitiesConfigFields.accountNumber,
              ...getConditionalArray(withFHADetails, [
                liabilitiesConfigFields.existingCaseNumber,
                liabilitiesConfigFields.existingLienUpfrontMip,
                liabilitiesConfigFields.existingLienInterestAmount,
                liabilitiesConfigFields.hudAuthorizationCode,
              ]),
              ...getConditionalArray(withMortgageDetails, () => [
                liabilitiesConfigFields.sourceOfFunds,
                ...getConditionalArray(isOtherSourceOfFunds, [
                  liabilitiesConfigFields.otherFundDescription,
                ]),
                liabilitiesConfigFields.monthsDeferred,
                {
                  ...liabilitiesConfigFields.lienPriorityType,
                  inputProps: {
                    ...liabilitiesConfigFields.lienPriorityType.inputProps,
                    required: isSubjectProperty,
                    options: lienPriorityTypeOptions,
                  },
                },
              ]),
              ...getConditionalArray(withChattelHomeLien && isSubjectProperty, [
                {
                  ...liabilitiesConfigFields.lienPriorityType,
                  inputProps: {
                    ...liabilitiesConfigFields.lienPriorityType.inputProps,
                    options: lienPriorityTypeOptions,
                  },
                },
              ]),
            ]),
          ],
          key: `${recordId}-expense`,
        },
        {
          type: FieldType.GROUP,
          fields: [
            ...getConditionalArray(isLiabilityType, [liabilitiesConfigFields.unpaidBalance]),
            ...getConditionalArray(isOtherLiabilityType, [
              liabilitiesConfigFields.liabilityExpenseType,
            ]),
            {
              ...liabilitiesConfigFields.monthlyPayment,
              inputProps: {
                ...liabilitiesConfigFields.monthlyPayment.inputProps,
                label: (
                  <>
                    Monthly Payment
                    <IconButton
                      aria-label="Calculate monthly payment"
                      data-testid={FinancialInfoTestIds.CALCULATE_MONTHLY_PAYMENT_BUTTON}
                      onClick={onCalculateMonthlyPayment}
                    >
                      <CalculatorIcon size={12} />
                    </IconButton>
                  </>
                ),
              },
            },
            ...getConditionalArray(isLiabilityType, () => [
              ...getConditionalArray(withHighLimit, [liabilitiesConfigFields.highLimit]),
              ...getConditionalArray(withRemainingMonths, [
                liabilitiesConfigFields.remainingMonths,
              ]),
              ...getConditionalArray(!isMortgageClicked && withProperty, [
                {
                  ...liabilitiesConfigFields.propertyAddress,
                  inputProps: {
                    ...liabilitiesConfigFields.propertyAddress.inputProps,
                    options: propertiesOptions,
                  },
                },
              ]),
            ]),
            liabilitiesConfigFields.dateOpened,
            ...getConditionalArray(isExcludePayment, () => [
              liabilitiesConfigFields.paymentExclusionReason,
              ...getConditionalArray(isOtherExclusionReason, [
                liabilitiesConfigFields.paymentExclusionReasonOtherDescription,
              ]),
            ]),
          ],
          key: `${recordId}-main`,
        },
        {
          type: FieldType.GROUP,
          fields: [
            ...getConditionalArray(isLiabilityType, [
              {
                ...liabilitiesConfigFields.paidOff,
                inputProps: {
                  ...liabilitiesConfigFields.paidOff.inputProps,
                  disabled: shouldForcePaidOffAndNonCashOut,
                },
              },
              liabilitiesConfigFields.excludePayment,
            ]),
            {
              ...liabilitiesConfigFields.isSubordinate,
              inputProps: {
                ...liabilitiesConfigFields.isSubordinate.inputProps,
                disabled: withMortgageDetails && lienPriorityType === LienPriorityType.FIRST,
              },
            },
            ...getConditionalArray(withHELOC, [liabilitiesConfigFields.heloc]),
            ...getConditionalArray(isPaidOff, [
              {
                ...liabilitiesConfigFields.qualifiedNonCashOut,
                inputProps: {
                  ...liabilitiesConfigFields.qualifiedNonCashOut.inputProps,
                  disabled: shouldForcePaidOffAndNonCashOut,
                },
              },
            ]),
            ...getConditionalArray(withMortgageDetails, [
              liabilitiesConfigFields.isAffordableLoan,
              liabilitiesConfigFields.isOriginalLenderRefinance,
            ]),
          ],
          key: `${recordId}-checkboxes`,
        },
        ...getConditionalArray(withSubjectPropertyMortgageDetails, [
          {
            type: FieldType.GROUP,
            fields: [
              liabilitiesConfigFields.closingCosts,
              liabilitiesConfigFields.originalPropertyValue,
              liabilitiesConfigFields.servicingContactName,
              liabilitiesConfigFields.servicingContactPhoneNumber,
            ],
            key: `${recordId}-subjectPropertyMortgage`,
          },
          {
            type: FieldType.GROUP,
            fields: [
              liabilitiesConfigFields.loanMipFactor,
              liabilitiesConfigFields.endorsementDate,
              liabilitiesConfigFields.originalEndorsementDate,
            ],
            key: `${recordId}-mipDetails`,
          },
          {
            type: FieldType.GROUP,
            fields: [
              liabilitiesConfigFields.unearnedMipFactor,
              liabilitiesConfigFields.unearnedMip,
              liabilitiesConfigFields.earnedMip,
            ],
            key: `${recordId}-mipCalculated`,
          },
        ]),
        ...getConditionalArray(isHeloc, [
          {
            type: FieldType.GROUP,
            fields: [liabilitiesConfigFields.highLimit],
            key: `${recordId}-conditional`,
          },
        ]),
      ],
    };
  },
);
